import type { CSSObjectWithLabel, MenuPlacement, MenuPosition } from 'react-select';

/**
 * CRPD-7358 — how an inline table-cell dropdown floats.
 *
 * Every table that edits in place puts its select inside a cell, and every one of those tables is a
 * scroll container: `GenericTable`'s wrapper is `overflow-y: auto` and needs `overflow-x: auto` for
 * its width, and `TierTable`'s `Wrap` is `overflow-x: auto` — which forces `overflow-y` to `auto`
 * too, because one non-visible axis takes the other with it. A menu positioned in normal flow is
 * therefore clipped by the table on every one of these surfaces, and react-select responds by
 * scrolling the page to try to reveal it: opening the last row's maturity dropdown on the RMP moved
 * `.page-content` 719px and took the whole table off screen (CRPD-7358, Henry 4 Sep).
 *
 * `menuPosition: 'fixed'` is the fix and the only load-bearing member here. react-select routes the
 * menu through `MenuPortal` whenever `menuPortalTarget || menuPosition === 'fixed'`, and with no
 * `appendTo` that path renders the menu **in place** rather than portalling it: it stays a
 * descendant of the styled select, so every `.cydea-select__*` rule still reaches it, while
 * `position: fixed` puts it outside the reach of any ancestor's `overflow`. Portalling to `<body>`
 * would escape the same clip and lose all of that styling — the menu would render bare, with the
 * table's rows showing through it.
 *
 * ONE object, spread by every inline cell, so the Risk Management table and the Control Maturity
 * table cannot drift into behaving differently — the ticket asks for both to feel the same.
 */

/**
 * Above the table's sticky `thead` (9) and the app's persistent header bar (11); the same layer the
 * RMP's other inline-cell popovers already occupy (`FrameworkCell.styles.ts`, `LinkCell.styles.ts`),
 * so a floating menu and a floating popover sit together. Still far below toasts (99999).
 */
const MENU_Z_INDEX = 2000;

/*
 * Keeping the open menu welded to its cell.
 *
 * A menu in normal flow — the framework picker on the control page is the one to compare against —
 * needs none of this: the browser moves it with its anchor, in the same paint, for free. A fixed
 * menu cannot be, and a fixed menu is what escaping the table's clip costs us. So its position has
 * to be written by hand, and the only real question is when.
 *
 * react-select writes it through floating-ui and a React render, which lands a frame after the row
 * has already moved — that is the trailing this ticket kept running into, and doing the same work
 * in `requestAnimationFrame` is no better, because that callback runs after the frame that moved
 * the row too. Scroll handlers run BEFORE the browser paints, so writing the offsets there puts the
 * menu and its row in the same paint, with no frame in which they disagree.
 *
 * A scroll handler alone is not enough, though, and the reason is easy to miss: react-select styles
 * the wrapper through Emotion, i.e. a generated CLASS (`getStyleProps` returns `{css, className}`,
 * `index-…:104-113`), while this writes the element's inline style. Inline always wins, so from the
 * first write onwards react-select's own corrections can no longer show — and its trigger set is
 * wider than scroll and resize, because floating-ui's `autoUpdate` also watches for layout shifts
 * and element resizes (`@floating-ui/dom:793-796`). Having taken those corrections away, this has
 * to cover the same ground: hence the frame loop, which re-reads the rect however the cell came to
 * move. The loop only runs while a menu is open.
 *
 * Two tidier answers were tried and rejected by use: closing the menu on scroll makes it vanish
 * mid-interaction, and holding the page still stops the page scrolling at all. react-select's own
 * `menuShouldBlockScroll` could not have done the latter regardless — its lock only applies
 * `overflow: hidden` to `document.body` (`Select-…:740`), and nothing here scrolls the body.
 */
const PORTAL_CLASS = 'cydea-select__menu-portal';
const MENU_CLASS = 'cydea-select__menu';
const CONTROL_CLASS = 'cydea-select__control';

/**
 * The select being tracked, resolved once per open.
 *
 * Held as elements rather than re-queried each frame, because the class names are not unique: every
 * select in the app carries the `cydea-select` prefix, and `--menu-is-open` lands on any control
 * whose menu is showing. A document-wide lookup would happily pin this menu to a different select's
 * control. The wrapper, by contrast, is only ever emitted for a fixed or portalled menu — which
 * here means an inline cell — so it is the one safe way in, and the control is then read from the
 * wrapper's OWN container rather than from the document.
 */
let tracked: { portal: HTMLElement; control: HTMLElement; hangsFrom: 'top' | 'bottom' } | null = null;
let frameId = 0;

/** Capture, so a scroll in any container is seen; passive, since nothing here cancels it. */
const TRACK_OPTIONS: AddEventListenerOptions = { capture: true, passive: true };

const pinMenuToControl = (): boolean => {
  if (!tracked) return false;
  const { portal, control, hangsFrom } = tracked;
  // Self-healing: a row can re-render away while its menu is open — the table refetching after a
  // mutation, say — and `onMenuClose` then never runs.
  if (!portal.isConnected || !control.isConnected) return false;
  const rect = control.getBoundingClientRect();
  portal.style.top = `${hangsFrom === 'top' ? rect.top : rect.bottom}px`;
  portal.style.left = `${rect.left}px`;
  portal.style.width = `${rect.width}px`;
  return true;
};

const trackMenuPosition = (): void => {
  if (!pinMenuToControl()) stopPinningMenu();
};

const trackEveryFrame = (): void => {
  trackMenuPosition();
  if (tracked) frameId = requestAnimationFrame(trackEveryFrame);
};

function stopPinningMenu(): void {
  document.removeEventListener('scroll', trackMenuPosition, TRACK_OPTIONS);
  window.removeEventListener('resize', trackMenuPosition, TRACK_OPTIONS);
  if (frameId) cancelAnimationFrame(frameId);
  frameId = 0;
  tracked = null;
}

const startPinningMenu = (): void => {
  stopPinningMenu();
  document.addEventListener('scroll', trackMenuPosition, TRACK_OPTIONS);
  window.addEventListener('resize', trackMenuPosition, TRACK_OPTIONS);
  /*
   * Nothing is pinned until this has run: placement can only be read once react-select has measured
   * and drawn the menu, which happens after `onMenuOpen`, and pinning on a guess would hang the
   * menu off the wrong edge for a frame. `tracked` staying null until then is what holds the
   * handlers off.
   *
   * Read placement from the MENU, never the wrapper around it: the wrapper is a zero-height box
   * whose top react-select parks on the control's own top edge for a flipped menu, so comparing IT
   * against the control reads "below" either way and the menu ends up over the cell.
   */
  frameId = requestAnimationFrame(() => {
    const portal = document.querySelector<HTMLElement>(`.${PORTAL_CLASS}`);
    const menu = portal?.querySelector<HTMLElement>(`.${MENU_CLASS}`);
    const control = portal?.parentElement?.querySelector<HTMLElement>(`.${CONTROL_CLASS}`);
    if (!portal || !menu || !control) {
      stopPinningMenu();
      return;
    }
    tracked = {
      portal,
      control,
      hangsFrom: menu.getBoundingClientRect().top < control.getBoundingClientRect().top ? 'top' : 'bottom',
    };
    trackEveryFrame();
  });
};

/** Exported for the test only. */
export { pinMenuToControl, startPinningMenu, stopPinningMenu, trackMenuPosition };

/** What the shared stylesheet caps every menu list at, kept as the preferred height. */
const PREFERRED_MENU_HEIGHT = 400;

/**
 * How much room below the cell is worth opening into, rather than flipping above it.
 *
 * These menus run 190-220px tall (four to seven options), so 220 reads as: if the whole menu will
 * not fit below, put it above instead of squashing it into a scrolling stub. A squashed menu is not
 * broken — it scrolls — but it looks cut off, which is what was reported.
 *
 * The figure is a balance, not a preference. react-select constrains a FLIPPED menu to the room
 * above it only while that room is at least this figure; below it, the full preferred height is
 * drawn above the cell whether or not it fits, and a fixed menu that overshoots the top of the
 * window cannot be scrolled back. That misbehaviour needs less than this much room BOTH above and
 * below at once — a viewport under roughly 490px. Setting this to the preferred 400 instead put the
 * threshold at ~850px, inside the range of an ordinary window; 220 keeps it well outside one.
 */
const MIN_ROOM_TO_OPEN_DOWNWARDS = 220;

export const INLINE_SELECT_MENU_PROPS = {
  menuPosition: 'fixed' as MenuPosition,
  menuPlacement: 'auto' as MenuPlacement,
  maxMenuHeight: PREFERRED_MENU_HEIGHT,
  /*
   * Flip rather than squash. See MIN_ROOM_TO_OPEN_DOWNWARDS above for why this figure is what it
   * is, and for the one misbehaviour it is chosen to stay clear of.
   */
  minMenuHeight: MIN_ROOM_TO_OPEN_DOWNWARDS,
  /* The menu is pinned to its cell frame by frame, so it cannot lag behind it — see the note above. */
  onMenuOpen: startPinningMenu,
  onMenuClose: stopPinningMenu,
  /*
   * Redundant while `menuPosition` is 'fixed' — react-select computes
   * `shouldScroll = menuShouldScrollIntoView && !isFixedPosition`, so fixed already zeroes it in
   * every placement branch. Kept as defence in depth: it is what would still stop the 719px page
   * jump if a later edit dropped `menuPosition` from this object.
   */
  menuShouldScrollIntoView: false,
  styles: {
    // react-select hardcodes `zIndex: 1` on the wrapper it positions; without this the floating
    // menu paints under the sticky header chrome.
    menuPortal: (base: CSSObjectWithLabel): CSSObjectWithLabel => ({ ...base, zIndex: MENU_Z_INDEX }),
    /*
     * react-select works out how tall the list may be from the space actually left on screen, and
     * hands that figure to this style. The shared stylesheet then throws it away: its flat
     * `.cydea-select__menu-list { max-height: 400px }` is a class-DESCENDANT rule, which outranks
     * the single class this emits, so the list drew its full height regardless and a menu opened
     * near the bottom of the window ran off it with its last option unreachable — no scrollbar,
     * because as far as the list was concerned it had all the room it asked for.
     *
     * `!important` is what wins that, and it is the same lever the inline-cell overrides in
     * GTCInlineWrapper.styles.ts already pull against this stylesheet. The preferred height is
     * unchanged at 400 (`maxMenuHeight` above), so a menu with room to open is exactly as tall as
     * it always was; the figure only bites when there genuinely is not room.
     */
    menuList: (base: CSSObjectWithLabel, props: { maxHeight: number }): CSSObjectWithLabel => ({
      ...base,
      maxHeight: `${props.maxHeight}px !important`,
    }),
  },
};
